import { useState } from 'react';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_string,
  ImpersonationDTO,
  LoginTwoFactorDTO,
  LoginUserDTO,
  TokenExchangeDTO,
  AuthClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseAuthApi {
  generateToken: (data: LoginUserDTO) => Promise<ApiResponse_string>;
  generateTokenTwoFactor: (
    data: LoginTwoFactorDTO,
  ) => Promise<ApiResponse_string>;
  googleTokenExchange: (data: TokenExchangeDTO) => Promise<ApiResponse_string>;
  impersonateMemberLogin: (
    data: ImpersonationDTO,
  ) => Promise<ApiResponse_string>;
  impersonateMemberLogout: (
    data: ImpersonationDTO,
  ) => Promise<ApiResponse_string>;
  isLoading: boolean;
  error: string | null;
}

export const useAuthApi = (): UseAuthApi => {
  const apiClient = new AuthClient(API_BASE_URL, axiosInstance);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const generateToken = async (
    data: LoginUserDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.generateToken(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error generating token', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const generateTokenTwoFactor = async (
    data: LoginTwoFactorDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.generateTokenTwoFactor(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error generating token with two factor', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const googleTokenExchange = async (
    data: TokenExchangeDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.googleTokenExchange(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error exchanging Google token', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateMemberLogin = async (
    data: ImpersonationDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.impersonateMemberLogin(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error impersonating member login', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const impersonateMemberLogout = async (
    data: ImpersonationDTO,
  ): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.impersonateMemberLogout(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error impersonating member logout', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateToken,
    generateTokenTwoFactor,
    googleTokenExchange,
    impersonateMemberLogin,
    impersonateMemberLogout,
    isLoading,
    error,
  };
};
